/* Product card image slider — global.
   Extracted verbatim from sections/wd-main-collection.liquid so the collection
   grid and the PDP product sliders share one implementation. Two IIFEs, in the
   original order: (1) the colour-aware slider, (2) the lazy image hydrator. */

/*
  PRODUCT IMAGE SLIDER — collection grid.
  Rendered ONCE in the section (not per card). Swipe + keyboard supported. No hover-swap.

  COLOUR-AWARE (same grouping logic as the PDP): each card's images are grouped by colour.
  A colour's swatch featured-media is the anchor (first image) of that colour's group; the
  group runs up to the next colour's anchor, with trailing orphan images (left over from
  deleted/unpublished variants) trimmed by filename stem. Clicking a swatch or using the
  arrows shows ONLY the selected colour's images, and images are hydrated lazily (only the
  current + neighbouring image of the active colour), so the initial page load is unchanged.
*/
(function () {
  'use strict';

  if (!window._pSliderRegistry) window._pSliderRegistry = new WeakSet();

  function loadImg(img, slide) {
    if (!img || img.dataset.loaded) return;
    img.dataset.loaded = '1';

    var src    = img.dataset.src;
    var srcset = img.dataset.srcset;
    var sizes  = img.dataset.sizes;

    if (sizes)  img.sizes  = sizes;
    if (srcset) img.srcset = srcset;
    if (src)    img.src    = src;

    function reveal() {
      img.classList.add('loaded');
      var ph = slide.querySelector('.slide-placeholder');
      if (ph) ph.classList.add('hidden');
    }

    if (img.complete && img.naturalWidth) {
      reveal();
    } else {
      img.addEventListener('load',  reveal, { once: true });
      img.addEventListener('error', reveal, { once: true });
    }
  }

  /* ── Filename helpers, ported verbatim from the PDP colour-range logic ────────
     Used to trim trailing orphan images so a colour group never bleeds into the
     next colour when a variant was deleted/unpublished. */
  function fileNameOf(url) {
    if (!url) return '';
    var s = url.lastIndexOf('/');
    var p = s >= 0 ? url.slice(s + 1) : url;
    var q = p.indexOf('?'); if (q !== -1) p = p.slice(0, q);
    var h = p.indexOf('#'); if (h !== -1) p = p.slice(0, h);
    return p.toLowerCase();
  }
  function normalizeForStem(filename) {
    var dot = filename.lastIndexOf('.');
    var base = dot > 0 ? filename.slice(0, dot) : filename;
    return base.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
  }
  var TRAILING_INDEX_RE = /-?\d+(?:-[a-z]{1,4})?$/i;
  function deriveStem(filename) {
    var base = normalizeForStem(filename);
    var stripped = base.replace(TRAILING_INDEX_RE, '');
    return stripped.length >= 3 ? stripped : base;
  }
  function belongsToStem(filename, stem) {
    if (!stem) return true;
    var n = normalizeForStem(filename);
    if (n === stem) return true;
    if (n.indexOf(stem + '-') === 0) return true;
    if (n.replace(TRAILING_INDEX_RE, '') === stem) return true;
    return false;
  }

  /* Placeholder shown in a slide until its real image is hydrated (same data-URI the
     server used to render for every non-first slide). */
  var BLANK_GIF = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

  function srcsetFromUrl(u) {
    return [300, 500, 700, 900].map(function (w) {
      return u.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
    }).join(', ');
  }

  /* ── Phone-only gesture guards (shared by every card) ──────────────────────────
     Everything here is gated on PHONE_MQ below, so desktop and tablet keep the
     original axis lock byte for byte. Mouse and keyboard input never reach any of
     it — these are consulted from the touch handlers only.

     THE BUG THIS EXISTS FOR: on the search page, scrolling with the soft keyboard
     up made the card slider swipe instead of the page scrolling.

     `.product-image-slider` carries `touch-action: pan-y pinch-zoom` (theme.css),
     so on a normal vertical drag the browser starts scrolling on the first move and
     the following touchmoves stop being cancelable — preventDefault() below becomes
     a no-op and the slider physically cannot steal the gesture. That is the safety
     net. While the keyboard is opening or closing the browser is RESIZING THE VISUAL
     VIEWPORT rather than scrolling, so the net is not there: events stay cancelable,
     and the viewport origin moving under the finger cancels most of the drag out of
     the reported clientY. A straight vertical scroll then measures dy ~ 0 against the
     6-15px of sideways drift every thumb produces, the lock reads 'x', and the rest
     of the gesture is preventDefault()ed away from the page.

     No dx/dy ratio can rescue that window, because dy itself is what is wrong there.
     So the gesture is handed to the page outright while the keyboard is up or still
     settling, and swiping returns once the viewport is stable again. */
  var PHONE_MQ = window.matchMedia ? window.matchMedia('(max-width: 740px)') : null;
  function isPhone() { return !!(PHONE_MQ && PHONE_MQ.matches); }

  /* A soft keyboard takes 250px+ of viewport; the iOS URL bar that collapses on scroll
     takes ~50-100px. The threshold between them is what keeps ordinary scrolling from
     reading as a keyboard event and suppressing swipe for no reason. */
  var KB_MIN_PX = 120;
  var KB_SETTLE_MS = 450;   // covers the show/hide animation, plus a beat
  var kbChangedAt = 0;

  (function watchViewport() {
    var vv = window.visualViewport;
    if (!vv) return;
    var lastH = vv.height;
    vv.addEventListener('resize', function () {
      if (Math.abs(vv.height - lastH) >= KB_MIN_PX) kbChangedAt = Date.now();
      lastH = vv.height;
    });
  }());

  // Keyboard currently up: the visual viewport is a keyboard's worth shorter than the
  // layout viewport. Covers Android, where the keyboard stays up while the page scrolls.
  function keyboardOpen() {
    var vv = window.visualViewport;
    return !!vv && (window.innerHeight - vv.height) > KB_MIN_PX;
  }

  // ...or still animating away. Covers iOS, where the first drag dismisses the keyboard,
  // so by the time we are asked it may already be closing rather than open.
  function keyboardBusy() {
    return keyboardOpen() || (Date.now() - kbChangedAt) < KB_SETTLE_MS;
  }

  /* Build the non-first slides from the card's JSON manifest. The server renders ONE slide
     per card plus this manifest; we recreate the exact markup the Liquid loop used to output,
     so everything below (grouping, hydration, wrap clones) sees an unchanged structure.
     Runs once — the manifest is removed so clones of an initialised card no-op here. */
  function buildSlides(el) {
    var manifest = el.querySelector('script.wd-slider-manifest');
    if (!manifest) return;
    var data = null;
    try { data = JSON.parse(manifest.textContent); } catch (e) {}
    manifest.remove();
    if (!data || !data.media || !data.media.length) return;

    var container = el.querySelector('.slider-container');
    if (!container) return;

    var base = container.querySelectorAll('.slider-slide').length;
    var frag = document.createDocumentFragment();
    data.media.forEach(function (m, i) {
      var slide = document.createElement('div');
      slide.className = 'slider-slide';
      slide.setAttribute('data-index', String(base + i));

      var link = document.createElement('a');
      link.href = data.url || '#';
      link.setAttribute('data-instant', '');
      link.className = 'slider-image-link';
      link.tabIndex = -1;

      var ph = document.createElement('div');
      ph.className = 'slide-placeholder';

      var img = document.createElement('img');
      img.className = 'product-item__primary-image lazy-image';
      img.loading = 'lazy';
      img.decoding = 'async';
      // "a" is present only when the alt differs from the product title ("t");
      // an explicit empty string is preserved as-is.
      img.alt = (m.a !== undefined && m.a !== null) ? m.a : (data.t || '');
      if (m.w) img.width = m.w;
      if (m.h) img.height = m.h;
      img.setAttribute('data-media-id', m.id);
      img.src = BLANK_GIF;
      img.setAttribute('data-src', m.u);
      img.setAttribute('data-srcset', srcsetFromUrl(m.u));
      if (data.sizes) img.setAttribute('data-sizes', data.sizes);

      link.appendChild(ph);
      link.appendChild(img);
      slide.appendChild(link);
      frag.appendChild(slide);
    });
    container.appendChild(frag);
  }

  /* Move photos that sit outside their own colour's run next to it, before any grouping runs.

     A colour group here is a CONTIGUOUS range of slides — the track translates by slide index —
     so a photo the media library interleaved elsewhere cannot be reached at all. Elle's third
     Potpourri shot sits at position 8, between two Black shots, and the card showed 2 of its 3
     Potpourri photos. Rather than teach the whole slider about gappy groups, the stray is put
     back beside its colour here, before initSlider reads the slides — so by the time the
     grouping below runs, the colour runs really are contiguous.

     A slide moves only when its filename stem matches exactly ONE of the card's colours. Where
     two colours share a naming pattern nothing moves — the Winter hoodie's entire shoot is
     named "WINTER-BLK-…", including the photos assigned to Light Grey Mix, so its slides are
     ambiguous and stay put. That same test is what keeps "shop by colour" cards safe: they
     print only the swatches that survived the filter, so a photo belonging to a hidden colour
     could look unambiguous — but such a card carries a single swatch and is excluded by the
     two-colour requirement below, and where it carries two (a product with both "Black - Pink"
     and "Black - Baby Pink") those share a stem, so every candidate reads as ambiguous. */
  function regroupStrandedSlides(el) {
    var card = el.closest('product-item') || el.parentElement;
    if (!card) return;

    var slides = Array.prototype.slice.call(el.querySelectorAll('.slider-slide'));
    var N = slides.length;
    var swatches = Array.prototype.slice.call(card.querySelectorAll('.color-swatch__radio'));
    if (N < 3 || swatches.length < 2) return;

    function fileAt(i) {
      var img = slides[i] ? slides[i].querySelector('img') : null;
      if (!img) return '';
      return fileNameOf(img.getAttribute('data-src') || img.getAttribute('src') || '');
    }

    var mediaIndex = {};
    slides.forEach(function (s, i) {
      var img = s.querySelector('img');
      var id = img && img.getAttribute('data-media-id');
      if (id) mediaIndex[id] = i;
    });

    /* One entry per colour: where its run starts and how its files are named. The first swatch
       usually has no anchor of its own — its featured photo is the eager slide 0, which carries
       no media id — so its stem comes from that slide's own image. */
    var slide0Img = slides[0].querySelector('img');
    var slide0File = fileNameOf(
      (slide0Img && (slide0Img.getAttribute('data-wd-orig-src') || slide0Img.getAttribute('src'))) || ''
    );
    var leading = null;
    var anchored = [];
    swatches.forEach(function (sw, i) {
      var mid = sw.getAttribute('data-variant-featured-media');
      var idx = (mid && mediaIndex.hasOwnProperty(mid)) ? mediaIndex[mid] : -1;
      if (idx >= 0) {
        var stem = deriveStem(fileAt(idx));
        if (stem) anchored.push({ start: idx, stem: stem });
      } else if (i === 0 && slide0File) {
        var lead = deriveStem(slide0File);
        if (lead) leading = { start: 0, stem: lead };
      }
    });
    anchored.sort(function (a, b) { return a.start - b.start; });
    if (!anchored.length) return;

    // Every colour's run, using the same rules as the grouping below.
    var runs = [];
    if (leading && anchored[0].start > 0) runs.push({ start: 0, end: anchored[0].start - 1, stem: leading.stem });
    anchored.forEach(function (a, i) {
      var cap = (i < anchored.length - 1) ? anchored[i + 1].start - 1 : N - 1;
      var end = a.start;
      for (var k = a.start; k <= cap; k++) if (belongsToStem(fileAt(k), a.stem)) end = k;
      runs.push({ start: a.start, end: end, stem: a.stem });
    });

    var isAnchor = {};
    anchored.forEach(function (a) { isAnchor[a.start] = true; });

    /* Collect the moves first — indices shift as soon as anything is reordered. Slide 0 is
       never a candidate: it is the eagerly loaded image that B4B repaints with the card's own
       colour, so it duplicates a manifest slide and reads as that colour's stray, and the code
       below it (featuredIndex, restoreSlide0, the leading-block rule) all assume it stays put
       at index 0. */
    var moves = [];
    runs.forEach(function (r) {
      var strays = [];
      for (var i = 1; i < N; i++) {                    // never slide 0 (see the note above)
        if (i >= r.start && i <= r.end) continue;      // already home
        if (isAnchor[i]) continue;                     // another colour's anchor: never move it
        var f = fileAt(i);
        if (!belongsToStem(f, r.stem)) continue;
        var ambiguous = false;
        for (var j = 0; j < runs.length; j++) {
          if (runs[j] !== r && belongsToStem(f, runs[j].stem)) { ambiguous = true; break; }
        }
        if (!ambiguous) strays.push(slides[i]);
      }
      if (strays.length) moves.push({ after: slides[r.end], nodes: strays });
    });

    moves.forEach(function (m) {
      var ref = m.after;
      m.nodes.forEach(function (node) {
        if (node === ref) return;
        ref.insertAdjacentElement('afterend', node); // moves the existing node
        ref = node;
      });
    });

    if (moves.length) {
      Array.prototype.forEach.call(el.querySelectorAll('.slider-slide'), function (s, i) {
        s.setAttribute('data-index', String(i));
      });
    }
  }

  function initSlider(el) {
    if (window._pSliderRegistry.has(el)) return;
    window._pSliderRegistry.add(el);

    buildSlides(el);
    regroupStrandedSlides(el);

    var container = el.querySelector('.slider-container');
    if (!container) return;

    var slides = Array.prototype.slice.call(el.querySelectorAll('.slider-slide'));
    var N = slides.length;
    if (N === 0) return;

    var prevBtn = el.querySelector('.slider-arrow--prev');
    var nextBtn = el.querySelector('.slider-arrow--next');

    // Progress line strip: server-rendered as the slider's next sibling (below
    // the photo, lululemon-style). Fallback create for safety; find-first so
    // B4B clones of an initialised card reuse their cloned copy.
    var progress = el.nextElementSibling;
    if (!progress || !progress.classList.contains('wd-slider-progress')) {
      progress = document.createElement('div');
      progress.className = 'wd-slider-progress';
      progress.hidden = true;
      progress.setAttribute('aria-hidden', 'true');
      el.insertAdjacentElement('afterend', progress);
    }
    function setProgressActive(rel) {
      var bar = progress.firstElementChild;
      var len = aEnd - aStart + 1;
      // Cumulative fill: image k of N covers k/N of the width.
      if (bar && len > 0) bar.style.width = ((rel + 1) * 100 / len) + '%';
    }
    function syncProgress() {
      var len = aEnd - aStart + 1;
      if (len <= 1) { progress.hidden = true; return; } // mirror arrow hiding
      progress.hidden = false;
      if (!progress.firstElementChild) progress.appendChild(document.createElement('span'));
      setProgressActive(cur - aStart);
    }

    function fileOf(i) {
      var img = slides[i] ? slides[i].querySelector('img') : null;
      if (!img) return '';
      return fileNameOf(img.getAttribute('data-src') || img.getAttribute('src') || '');
    }

    // media id -> slide index (data-media-id lives on each slide's <img>).
    var mediaIndex = {};
    slides.forEach(function (s, i) {
      var img = s.querySelector('img');
      var id = img && img.getAttribute('data-media-id');
      if (id) mediaIndex[id] = i;
    });

    /* ── Build per-colour ranges from the card's swatches ───────────────────── */
    var card = el.closest('product-item') || el.parentElement;
    var swatches = card
      ? Array.prototype.slice.call(card.querySelectorAll('.color-swatch__radio'))
      : [];

    /* Slide 0 is special: it is the eagerly rendered featured image, and B4B REPAINTS it with
       the colour that card represents. `data-wd-orig-src` (server-rendered) is what the theme
       itself put there — the product's default photo, which on a colour-filtered card is a
       DIFFERENT colour. Both are needed while grouping, so read them before the ranges are
       built (the restore below uses the same two values). */
    var slide0Img  = slides[0] && slides[0].querySelector('img');
    var slide0Orig = slide0Img && slide0Img.getAttribute('data-wd-orig-src');
    // The colour B4B has assigned to this whole card, when it is one of its per-colour cards.
    var b4bColor = card && card.dataset && card.dataset.b4bColor
      ? card.dataset.b4bColor.toLowerCase().trim()
      : '';

    /* A COLOUR-FILTERED card: B4B rendered this card for one colour and the theme printed only
       the swatches that survived the filter ("shop by colour" collections — sometimes two, e.g.
       a product with both "Black - Pink" and "Black - Baby Pink"). Slide 0 is then the
       product's default photo — whichever colour Shopify happens to order first — so it must
       not be assumed to belong to this card's colour. The test is evidence-based rather than a
       swatch count: the card's own swatches anchor real images, and slide 0's own photo is not
       one of them. On ordinary collections the first colour's anchor IS slide 0, which the
       branch above handles, so nothing there changes. */
    var colourFiltered = !!b4bColor;

    /* Is slide 0's OWN photo (not an app repaint) exactly the image at `i`? Deliberately an
       identity test, not a stem match: colour ranges here are separated by ANCHOR POSITION,
       and filenames cannot be trusted to separate them. On the Winter hoodie the whole
       shoot is named "WINTER-BLK-…", including the photo assigned to Light Grey Mix — a stem
       test calls that black and puts the grey photo on the black card. */
    function slide0IsImageAt(i) {
      if (!slide0Orig) return false;
      return fileNameOf(slide0Orig) === fileOf(i);
    }

    // Does slide 0's OWN photo belong to the group anchored at `start`? On colour-filtered
    // cards only an exact match counts (see above); elsewhere the filename stem — the same
    // signal used to trim trailing orphans below — is enough.
    // A card carrying a SINGLE swatch is colour-filtered even when `data-b4b-color` is not
    // there yet: the colour collections server-render each card with just the surviving
    // colour's swatch, and B4B only stamps its attribute (and repaints slide 0) some time
    // after load. When this init won the race against that stamp, the b4bColor flag alone
    // read these cards as ordinary — and the reach-back below swallowed every other
    // colour's photos into the one swatch's group (the mint collection bug: a card whose
    // colour anchors late in the strip paged through the whole product from slide 0).
    function slide0BelongsToGroupAt(start) {
      if (!slide0Orig) return true;              // nothing to compare: keep the old behaviour
      if (colourFiltered || swatches.length === 1) return slide0IsImageAt(start);
      var stem = deriveStem(fileOf(start));
      if (!stem) return true;
      return belongsToStem(fileNameOf(slide0Orig), stem);
    }

    var swatchValues = {};
    swatches.forEach(function (sw) { swatchValues[(sw.value || '').toLowerCase().trim()] = true; });

    /* A colour's ANCHOR is the slide index where its images start. Prefer the image's own
       colour tag in the alt text (Focal/PDP "#OptionName_Value" convention — the same signal
       the PDP groups by); fall back to the variant featured-media id. Relying on featured-media
       alone missed colours whose variants had no assigned variant image: that colour got no
       range and rangeForColor fell through to ANOTHER colour, so the first swatch showed the
       second colour's images. */
    function altColorOf(i) {
      var img = slides[i] && slides[i].querySelector('img');
      if (!img) return null;
      var alt = img.getAttribute('alt') || '';
      var hash = alt.lastIndexOf('#');
      if (hash < 0) return null;
      var tag = alt.slice(hash + 1);          // e.g. "Color_Navy"
      var us = tag.indexOf('_');
      if (us < 0) return null;
      var val = tag.slice(us + 1).toLowerCase().trim();
      return swatchValues[val] ? val : null;  // ignore tags that aren't one of THIS card's colours
    }

    var altAnchors = {};
    slides.forEach(function (s, i) {
      var col = altColorOf(i);
      if (col && !(col in altAnchors)) altAnchors[col] = i;
    });

    var anchors = [];
    swatches.forEach(function (sw) {
      var color = (sw.value || '').toLowerCase().trim();
      var idx = (color in altAnchors) ? altAnchors[color] : -1;
      if (idx < 0) {
        var mid = sw.getAttribute('data-variant-featured-media');
        if (mid && mediaIndex.hasOwnProperty(mid)) idx = mediaIndex[mid];
      }
      if (idx >= 0) anchors.push({ color: color, start: idx });
    });
    anchors.sort(function (a, b) { return a.start - b.start; });

    var ranges = {};
    anchors.forEach(function (a, i) {
      var start = a.start;
      var end = (i < anchors.length - 1) ? anchors[i + 1].start - 1 : N - 1;
      /* Trim trailing orphans (photos of deleted/unpublished variants that sit past the end of
         this colour's run) by ending on the LAST image sharing this colour's stem — not on the
         first image that doesn't.

         Breaking at the first mismatch assumed a colour's files are named identically, and they
         are not: one file inside Rum Raisin's run is "FW25-ELLE--RUM_RAISIN…" with a double
         dash, Sky's black run mixes "-BLK-S-" with "-BLK-XS-", and some shots are named after
         the other garment in the photo. Each such file truncated its group on the spot and
         every photo after it disappeared from the slider — 8 colour groups across the
         catalogue, losing 2-5 photos each.

         Scanning to the last match cannot wander into another colour: `end` is already capped
         at the next colour's anchor. An oddly named file inside the run is simply carried
         along (it is that colour's photo), while the unanchored leftovers past the run still
         fall outside, because nothing after them matches the stem. */
      var stem = deriveStem(fileOf(start));
      if (stem) {
        var lastMatch = start;
        for (var k = start; k <= end; k++) {
          if (belongsToStem(fileOf(k), stem)) lastMatch = k;
        }
        end = lastMatch;
      }
      ranges[a.color] = { start: start, end: end };
    });

    /* Make sure slide 0 (and any leading images) belong to a colour, so the image shown on load
       is never in a gap. If the FIRST swatch in display order has no anchor of its own but images
       sit before the first anchored colour, those leading images are that first swatch's — this
       recovers a first colour that has neither an alt tag nor a variant image (the reported bug).
       Otherwise the first anchored colour simply owns everything from slide 0.

       NOT on a colour-filtered card, though. On "shop by colour" collections B4B renders one card
       per colour and the theme prints a single swatch, so `anchors[0]` is that colour — and
       extending it down to slide 0 swallowed every image before it, which are OTHER colours'
       photos (the black collection's Bianca card ran from the pink shots through the black ones).
       Only reach back to slide 0 when slide 0's own photo really is this colour's. When it is
       not, the group stays at its anchor — visually identical on load, because B4B painted slide
       0 with exactly that anchor's image — and the arrows stay inside the one colour.
       Ordinary cards keep the original reach-back: their first colour has no anchor of its own
       (its featured image IS slide 0, which carries no media id), so they take the branch above.

       The belongs-check runs UNCONDITIONALLY — not only when B4B has stamped the card. The
       colour collections server-render their cards already filtered (one swatch, slide 0 the
       product's DEFAULT photo in another colour), and B4B stamps `data-b4b-color` after load;
       whenever this init ran first, the old `!colourFiltered` shortcut reached back anyway and
       the single colour's group ran from slide 0 through every other colour's photos. On
       ordinary cards the check keeps the reach-back (slide 0 shares the first colour's stem),
       so nothing changes there.

       `slide0Owned` records the outcome for restoreSlide0 below: it may only put the original
       photo back on a slide 0 that some colour group legitimately claimed here. */
    var slide0Owned = false;
    if (anchors.length) {
      var firstColor = swatches.length ? (swatches[0].value || '').toLowerCase().trim() : null;
      if (firstColor && !ranges[firstColor] && anchors[0].start > 0) {
        ranges[firstColor] = { start: 0, end: anchors[0].start - 1 };
        slide0Owned = true;
      } else if (slide0BelongsToGroupAt(anchors[0].start)) {
        ranges[anchors[0].color].start = 0;
        slide0Owned = true;
      }
    }

    /* A colour-filtered card whose swatch carries no anchor at all (no alt colour tag and no
       variant featured media) would otherwise get no range, and init falls back to "every image
       in the product" — the same wrong-colour gallery by another route. Derive its group from
       the photo B4B painted onto slide 0: the contiguous run of slides sharing that file's stem,
       or slide 0 alone when nothing matches. Never the whole product. */
    if (b4bColor && !ranges[b4bColor] && slide0Img) {
      var paintedStem = deriveStem(fileNameOf(slide0Img.getAttribute('src') || ''));
      var from = -1;
      if (paintedStem) {
        for (var s0 = 1; s0 < N; s0++) {
          if (belongsToStem(fileOf(s0), paintedStem)) { from = s0; break; }
        }
      }
      if (from > 0) {
        /* Walk the run, stepping over the odd inconsistently named file (Sky's black run is
           …BLK-S-027, …BLK-S-028, …BLK-**XS**-021, …BLK-S-022) but stopping as soon as the
           naming does not resume within a couple of slides — that is the next colour starting.
           Unlike the anchored groups above there is no next anchor to cap the scan here, so it
           cannot simply run to the last match: a short lookahead is what keeps it inside the
           colour. */
        var LOOKAHEAD = 2;
        var to = from;
        for (var s1 = from; s1 < N; s1++) {
          if (belongsToStem(fileOf(s1), paintedStem)) { to = s1; continue; }
          var resumes = false;
          for (var s2 = s1 + 1; s2 <= s1 + LOOKAHEAD && s2 < N; s2++) {
            if (belongsToStem(fileOf(s2), paintedStem)) { resumes = true; break; }
          }
          if (!resumes) break;
        }
        // Slide 0 joins the group only when its own photo IS the photo B4B painted — i.e. the
        // theme's default image already was this colour — so the card keeps the very image it
        // loaded with. Anything less certain would risk another colour's photo again.
        var slide0IsThisColour =
          fileNameOf(slide0Img.getAttribute('src') || '') === fileNameOf(slide0Orig || '');
        ranges[b4bColor] = { start: slide0IsThisColour ? 0 : from, end: to };
        if (slide0IsThisColour) slide0Owned = true;
      } else {
        ranges[b4bColor] = { start: 0, end: 0 };
      }
    }

    /* The same card BEFORE B4B has stamped it: a server-filtered single-swatch card whose
       colour anchored nothing (no alt tag, no variant featured media). Without a range the
       init below falls through to "every image in the product" — the wrong-colour gallery
       again, and permanently: the late-swap observer can only re-open colours that have a
       range. Slide 0 alone is the safe group — B4B repaints it with this card's colour —
       EXCEPT when the whole strip shares slide 0's filename stem: that is a one-colour
       product, whose full gallery genuinely belongs to the swatch. */
    if (!b4bColor && swatches.length === 1) {
      var soleColor = (swatches[0].value || '').toLowerCase().trim();
      if (soleColor && !ranges[soleColor]) {
        var soleStem = deriveStem(fileOf(0));
        var allShareStem = !!soleStem;
        for (var s3 = 1; s3 < N; s3++) {
          if (!belongsToStem(fileOf(s3), soleStem)) { allShareStem = false; break; }
        }
        ranges[soleColor] = allShareStem ? { start: 0, end: N - 1 } : { start: 0, end: 0 };
        if (allShareStem) slide0Owned = true;
      }
    }

    /* Slide 0 is the eager featured image and, in manifest order, the FIRST colour's first
       image. The B4B "variable product" cards repaint it with the card's SELECTED colour photo
       (a different colour) and strip its colour hints. We must NOT drop slide 0 from its owning
       colour's range to work around that — doing so permanently skips the first colour's first
       image (clicking the first swatch would land on image 2). Instead slide 0 STAYS in its
       group, and when that group is actually shown we restore slide 0 to its own image via
       restoreSlide0() below. `data-wd-orig-src` (rendered server-side) survives the repaint, so
       the restore always has the correct first image to fall back to. */

    // Return null (not another colour) when a colour has no images, so a swatch never shows the
    // wrong colour — the click is simply a no-op.
    function rangeForColor(color) {
      return (color && ranges[color]) ? ranges[color] : null;
    }

    var aStart = 0, aEnd = N - 1, cur = 0, busy = false, settleTimer = null, pendingSettle = null;

    /* Restore slide 0 to its OWN image (see the "Slide 0 is the eager featured image" note above).
       B4B repaints slide 0 with the card's selected colour; when the user picks the FIRST colour
       we land on slide 0, so put its real first image back. Deferred to the moment slide 0 becomes
       visible (called from updateActive when cur === 0), so page load fires NO extra image request
       — the fetch happens only if/when the first colour is actually selected. `featObserver` (the
       late-swap watcher set up further down) is paused around our write so it never mistakes this
       restore for a B4B swap. */
    var featObserver = null;
    function restoreSlide0() {
      if (!slide0Img || !slide0Orig) return;
      if (fileNameOf(slide0Img.getAttribute('src') || '') === fileNameOf(slide0Orig)) return;
      /* Only when a colour group actually claimed slide 0 while the ranges were built. On a
         colour-filtered card nothing claims it (its photo is the product default, in another
         colour) — restoring it there is what showed navy Paige, pink Bianca and the grey
         Winter hoodie on the black collection. Winter is also why this is a recorded fact
         rather than another filename comparison: its whole shoot is named "WINTER-BLK-…",
         including the photo assigned to Light Grey Mix, so no stem test can separate them. */
      if (!slide0Owned) return;
      /* Elsewhere, put the original back only when it belongs to the group on screen. That
         group's identity is read from its LAST slide, never from slide 0, because slide 0 is
         the one an app repaints. */
      var groupStem = deriveStem(fileOf(aEnd > aStart ? aEnd : aStart));
      if (groupStem && !belongsToStem(fileNameOf(slide0Orig), groupStem)) return;
      if (featObserver) featObserver.disconnect();
      slide0Img.setAttribute('srcset', srcsetFromUrl(slide0Orig));
      slide0Img.src = slide0Orig;
      if (featObserver && featuredSlide) {
        featObserver.takeRecords(); // drop the records our own write just queued
        featObserver.observe(featuredSlide, {
          attributes: true, attributeFilter: ['src', 'srcset'], subtree: true, childList: true
        });
      }
    }

    // translate the track so slide `slot` fills the frame (slot may be -1 or N for wrap clones).
    function txFor(slot) { return 'translate3d(' + (-(slot * 100)) + '%, 0, 0)'; }
    function setRest() {
      container.style.setProperty('--wd-rest', (-(cur * 100)) + '%');
    }
    /* Parked-state isolation. While the track is at rest only the ACTIVE slide is
       painted (.wd-cur; neighbours get visibility:hidden via wd-card-image-slider.css).
       During an animated move the container carries .wd-moving so the incoming /
       outgoing slides render normally. This is what actually kills the "sliver of
       the next photo at the card edge" on real devices: with fractional card widths
       (1fr grid columns) the GPU parks the track at a fractional device-pixel
       offset and rasterises a hairline of the neighbour into the boundary pixel —
       an error the 1px clip-path can only mask per-DPR. A neighbour that is not
       painted at all cannot bleed, on any device. */
    var movingTimer = null;
    function markMoving() {
      container.classList.add('wd-moving');
      if (movingTimer) clearTimeout(movingTimer);
      // Self-cleanup a beat after the 350ms transition, for any path where
      // transitionend is swallowed (hidden tab, interrupted animation).
      movingTimer = setTimeout(unmarkMoving, 600);
    }
    function unmarkMoving() {
      if (movingTimer) { clearTimeout(movingTimer); movingTimer = null; }
      container.classList.remove('wd-moving');
    }

    function positionInstant() {
      container.style.transition = 'none';
      container.style.transform  = txFor(cur);
      container.offsetHeight; // force reflow
      container.style.transition = '';
      setRest();
      unmarkMoving();
    }
    function updateActive() {
      // When slide 0 is the one on screen, make sure it shows ITS image (not a B4B repaint).
      if (cur === 0) restoreSlide0();
      slides.forEach(function (s, i) {
        var a = s.querySelector('a');
        if (a) a.tabIndex = i === cur ? 0 : -1;
        s.classList.toggle('wd-cur', i === cur);
      });
      var multi = aEnd > aStart;
      el.dataset.wdMulti = multi ? '1' : '0';
      // Hide the arrows when the active colour has a single image.
      if (prevBtn) prevBtn.style.display = multi ? '' : 'none';
      if (nextBtn) nextBtn.style.display = multi ? '' : 'none';
      syncProgress();
    }
    function loadAround() {
      [cur - 1, cur, cur + 1].forEach(function (i) {
        if (i < aStart || i > aEnd) return;
        var img = slides[i] && slides[i].querySelector('img.lazy-image');
        if (img) loadImg(img, slides[i]);
      });
    }

    // transitionend / timeout -> run whatever was queued for after the move, then clear busy.
    function runSettle() {
      if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
      var cb = pendingSettle; pendingSettle = null;
      busy = false;
      if (cb) cb();
      unmarkMoving(); // after cb: wrap settles call positionInstant, which also unmarks
    }
    function onSettle(cb) {
      pendingSettle = cb;
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(runSettle, 450); // fallback if transitionend never fires
    }
    container.addEventListener('transitionend', function (e) {
      if (e.propertyName === 'transform') runSettle();
    });

    /* Absolute overlay used ONLY during a wrap animation. It is parked at a "virtual" slot
       (one past an end) so we can animate CONTINUOUSLY onto the first/last image of the colour
       group, then snap the real track back — no jerky rewind, no shifting of real slide indices. */
    /* Adopt a clone the card already carries. B4B expands one card into per-colour
       cards by CLONING the DOM, so a card cloned mid-wrap inherits a VISIBLE
       .slider-wrap-clone with a stale inline `left`. Without this the fresh
       initSlider would build a second clone and only ever hide that one, leaving
       the inherited one parked at the right edge forever — a permanent sliver of
       another image. (Same find-first pattern the progress strip already uses.) */
    var wrapClone = container.querySelector('.slider-wrap-clone') || null;
    if (wrapClone) {
      wrapClone.style.display = 'none';
      wrapClone.style.left = '0';
    }
    function ensureClone() {
      if (wrapClone) return wrapClone;
      wrapClone = document.createElement('div');
      wrapClone.className = 'slider-wrap-clone';
      wrapClone.setAttribute('aria-hidden', 'true');
      wrapClone.appendChild(document.createElement('img'));
      container.appendChild(wrapClone);
      return wrapClone;
    }
    function showClone(fromIndex, atSlot) {
      var lazy = slides[fromIndex].querySelector('img.lazy-image');
      if (lazy) loadImg(lazy, slides[fromIndex]);
      var srcImg = slides[fromIndex].querySelector('img');
      var clone = ensureClone();
      var cimg = clone.querySelector('img');
      if (srcImg) {
        cimg.src = srcImg.currentSrc || srcImg.src;
        if (srcImg.srcset) cimg.srcset = srcImg.srcset; else cimg.removeAttribute('srcset');
      }
      clone.style.left = (atSlot * 100) + '%';
      clone.style.display = 'block';
    }
    // Park it back at slot 0 as well as hiding it, so a stale `left` can never
    // resurface if anything flips `display` without going through showClone().
    function hideClone() {
      if (!wrapClone) return;
      wrapClone.style.display = 'none';
      wrapClone.style.left = '0';
    }

    // Switch the visible colour group. `landOn` (optional) keeps a specific slide in view
    // (e.g. the featured image on load); otherwise we land on the group's first image.
    function applyRange(r, animate, landOn) {
      if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
      pendingSettle = null;
      busy = false;
      hideClone();
      aStart = r.start;
      aEnd = r.end;
      cur = (typeof landOn === 'number' && landOn >= r.start && landOn <= r.end) ? landOn : r.start;
      updateActive();
      if (animate) {
        markMoving();
        container.style.transform = txFor(cur);
        setRest();
      } else {
        positionInstant();
      }
      loadAround();
    }

    function go(dir) {
      if (busy) return;
      var len = aEnd - aStart + 1;
      if (len <= 1) return;
      var rel = (cur - aStart) + dir;

      if (rel >= 0 && rel < len) {
        // Normal move within the colour group.
        busy = true;
        cur = aStart + rel;
        markMoving();
        container.style.transform = txFor(cur);
        setRest();
        updateActive();
        loadAround();
        onSettle(function () {});
        return;
      }

      // Wrap: animate CONTINUOUSLY onto a clone of the opposite end, then snap the track back.
      busy = true;
      markMoving();
      if (dir > 0) {
        showClone(aStart, aEnd + 1);        // first image, parked just past the last
        setProgressActive(0);               // optimistic: don't wait for settle
        container.style.transform = txFor(aEnd + 1);
        onSettle(function () {
          cur = aStart; positionInstant(); hideClone(); updateActive(); loadAround();
        });
      } else {
        showClone(aEnd, aStart - 1);        // last image, parked just before the first
        setProgressActive(aEnd - aStart);   // optimistic: don't wait for settle
        container.style.transform = txFor(aStart - 1);
        onSettle(function () {
          cur = aEnd; positionInstant(); hideClone(); updateActive(); loadAround();
        });
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        go(-1);
        prevBtn.blur();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        go(1);
        nextBtn.blur();
      });
    }

    /* Touch / swipe.
       Skipped when the card sits inside a slick carousel (the PDP product
       sliders): there a horizontal drag is the ROW's gesture, and having both
       respond means one swipe advances the card's photo AND scrolls the row.
       The arrows, keyboard and colour swatches still drive the gallery there;
       on the collection grid (no outer carousel) swipe works as before.
       The slick classes alone are not enough to test: these sliders are hidden
       and initialised after load, so a card can init BEFORE slick has stamped
       them — hence the wrapper class list too. */
    var inCarousel = inCarouselSection(el);

    if (!inCarousel) {
      /* The gesture is axis-locked. The first few pixels decide whether the touch belongs to
         the page (vertical -> we keep out of the way) or to the gallery (horizontal -> we
         cancel the touch so the page cannot scroll up and down while the customer swipes
         through the photos). Two details make the lock actually hold:
           - touchmove must be NON-passive, otherwise preventDefault() is ignored;
           - the decision has to land within the browser's touch slop, because once a scroll
             has started the events stop being cancelable. Hence the small threshold.
       On PHONES the thresholds are stricter and the lock is revocable — 6px was small enough
       that a vertical scroll read as a swipe roughly as often as not. See the phone-only
       branches below and the guards up by isPhone/keyboardBusy. */
      var touchX = 0, touchY = 0, axis = null; // null = undecided, then 'x' or 'y'
      var phoneGesture = false; // read once per gesture, so rotation is picked up

      container.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { axis = 'y'; return; } // pinch / multi-touch: hands off
        touchX = e.touches[0].clientX;
        touchY = e.touches[0].clientY;
        axis   = null;
        phoneGesture = isPhone();
        /* Phone with the keyboard up or still animating: the axis lock cannot be trusted
           here (see the note by keyboardBusy), so the gesture goes straight to the page.
           Scrolling past the products stays smooth; swiping comes back as soon as the
           viewport settles, which on iOS is the moment the keyboard has finished closing. */
        if (phoneGesture && keyboardBusy()) axis = 'y';
      }, { passive: true });

      container.addEventListener('touchmove', function (e) {
        if (axis === 'y' || !e.touches.length) return;
        var dx = e.touches[0].clientX - touchX;
        var dy = e.touches[0].clientY - touchY;
        if (axis === null) {
          /* Phones ask for more before the slider claims the gesture: a longer move, and a
             clear horizontal bias rather than a bare dx > dy coin flip. The original 6px
             lands inside the browser's own touch slop, where the ratio is mostly noise —
             a genuine vertical scroll with a normal thumb arc wins for 'x' about as often
             as not. Desktop and tablet keep the original numbers. */
          var slop = phoneGesture ? 10 : 6;
          if (Math.abs(dx) < slop && Math.abs(dy) < slop) return;
          if (phoneGesture) {
            axis = Math.abs(dx) > Math.abs(dy) * 1.5 ? 'x' : 'y';
          } else {
            axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          }
        } else if (phoneGesture && axis === 'x' && Math.abs(dy) > Math.abs(dx) * 1.5) {
          /* Late bail-out (phones only). The lock used to be final for the whole gesture, so
             one bad early sample held preventDefault down even while the finger travelled
             hundreds of pixels vertically. Once the drag is unambiguously a scroll, give it
             back — dropping preventDefault lets the browser resume its pan-y scroll. A real
             swipe never trips this: it would have to out-travel its own horizontal distance
             by half again. */
          axis = 'y';
          return;
        }
        if (axis === 'x' && e.cancelable) e.preventDefault();
      }, { passive: false });

      container.addEventListener('touchend', function (e) {
        var horizontal = axis === 'x';
        axis = null;
        if (!horizontal) return;
        var diff = touchX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1);
      }, { passive: true });

      container.addEventListener('touchcancel', function () {
        axis = null;
      }, { passive: true });
    }

    /* Keyboard */
    el.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  go(-1);
      if (e.key === 'ArrowRight') go(1);
    });

    // Point the card's product links at a given variant (mirrors the theme's own behaviour).
    function updateLinksToVariant(vid) {
      if (!vid || !card) return;
      card.querySelectorAll('a[href*="/products"]').forEach(function (link) {
        try {
          var u = new URL(link.href, window.location.origin);
          u.searchParams.set('variant', vid);
          link.setAttribute('href', u.toString());
        } catch (err) {}
      });
    }

    /* Colour swatches -> show that colour's images. We drive the gallery here, so stop the
       theme's default single-image swap (product-item.js hides every OTHER primary image,
       which would blank the slider). We still mirror its variant-link update ourselves. */
    swatches.forEach(function (sw) {
      sw.addEventListener('change', function (e) {
        if (!sw.checked) return;
        e.stopImmediatePropagation();
        updateLinksToVariant(sw.getAttribute('data-variant-id'));
        var r = rangeForColor((sw.value || '').toLowerCase().trim());
        if (r) applyRange(r, false);
      });
    });

    // Which colour group owns a given slide index.
    function entryContaining(idx) {
      for (var c in ranges) {
        if (ranges.hasOwnProperty(c) && idx >= ranges[c].start && idx <= ranges[c].end) {
          return { color: c, range: ranges[c] };
        }
      }
      return null;
    }

    /* INITIAL STATE
       Show the first image and move the swatch ring onto the colour that image belongs to, so
       the photo, the selected swatch and the arrow range always agree.

       The tricky part is reading the first image's colour. Normally it carries a "#Color_Value"
       alt tag or a data-media-id matching a swatch. But the per-colour "variable product" cards
       the B4B app renders swap in a first <img> that has NEITHER hint AND flip the selected
       swatch with a bare `.checked = true` (property, no event) on their own schedule. So we
       must NOT trust the checked-swatch state (it can still be the server default when we run,
       or get changed after us). Instead we read the colour straight from the first image:
         1. its own alt tag / media id, else
         2. match its FILE against the colour-anchored manifest slides and borrow their colour.
       Both are race-free — they read the pixels on screen, not a checkbox an app may flip. */
    var featuredImg = el.querySelector('.slider-slide img:not(.lazy-image)');
    var featuredIndex = 0;
    if (featuredImg) {
      var fi = slides.indexOf(featuredImg.closest('.slider-slide'));
      if (fi >= 0) featuredIndex = fi;
    }

    function resolvedColorOf(i) {
      var byAlt = altColorOf(i);
      if (byAlt) return byAlt;
      var img = slides[i] && slides[i].querySelector('img');
      var mid = img && img.getAttribute('data-media-id');
      if (mid) {
        for (var s = 0; s < swatches.length; s++) {
          if (swatches[s].getAttribute('data-variant-featured-media') === mid) {
            return (swatches[s].value || '').toLowerCase().trim();
          }
        }
      }
      // Fall back to matching the file: the manifest slides ARE colour-anchored, and an app that
      // swaps the featured image reuses one of the product's media files, so the same file shows
      // up (colour-tagged) further down the strip.
      var f = fileOf(i);
      if (f) {
        for (var j = 0; j < N; j++) {
          if (j !== i && fileOf(j) === f) {
            var e = entryContaining(j);
            if (e) return e.color;
          }
        }
      }
      return null;
    }

    // Open a colour group and move the swatch ring onto it so image + swatch agree. `landOn`
    // keeps a specific slide in view when it falls inside the group (else we land on its first).
    function openColor(color, landOn) {
      if (!color || !ranges[color]) return false;
      applyRange(ranges[color], false, landOn);
      swatches.forEach(function (sw) {
        var isMatch = (sw.value || '').toLowerCase().trim() === color;
        sw.checked = isMatch;
        if (isMatch) updateLinksToVariant(sw.getAttribute('data-variant-id'));
      });
      return true;
    }

    var featuredColor = resolvedColorOf(featuredIndex);
    var checkedSwatch = card ? card.querySelector('.color-swatch__radio:checked') : null;
    var checkedColor  = checkedSwatch ? (checkedSwatch.value || '').toLowerCase().trim() : null;
    // A card that already carries .variable-products has been PROCESSED by B4B — since
    // load-more started flushing cards into the grid hidden, that happens BEFORE this
    // init runs (we init on reveal, B4B expands while hidden). On such a card the checked
    // swatch is B4B's authoritative colour choice, and the first image is whatever B4B
    // repainted (often still carrying the ORIGINAL first colour's alt/file, which
    // resolvedColorOf happily misreads). Trusting the image over the checked swatch here
    // is what flipped freshly revealed cards from their correct swatch to the FIRST
    // swatch about a second after reveal. So: on processed B4B cards the checked swatch
    // wins; the image-derived colour stays the source of truth everywhere else.
    var b4bProcessed = card && card.classList && card.classList.contains('variable-products');
    if (b4bProcessed && checkedColor && openColor(checkedColor, featuredIndex)) {
      featuredColor = checkedColor; // baseline for the late-swap observer below
    } else if (featuredColor && ranges[featuredColor]) {
      openColor(featuredColor, featuredIndex);
    } else {
      // First image unreadable: best-effort from the checked swatch, then the leading anchor.
      if (!openColor(checkedColor)) {
        var entry = entryContaining(featuredIndex);
        if (entry) {
          if (b4bProcessed) {
            // Never re-check a different swatch on a processed B4B card — show the
            // group the featured image sits in but leave B4B's selection alone.
            applyRange(entry.range, false, featuredIndex);
          } else {
            openColor(entry.color, featuredIndex);
          }
        } else {
          var initColor = checkedColor || (anchors[0] && anchors[0].color);
          applyRange(rangeForColor(initColor) || { start: 0, end: N - 1 }, false);
        }
      }
    }

    /* Reconcile late app swaps. B4B (and similar) may replace the first image and re-select a
       colour AFTER we initialise — flipping the swatch with a bare `.checked =` that fires no
       event we can hook. The image swap DOES mutate the first slide's <img>, so watch it and
       re-open whichever colour the new first image belongs to, keeping photo and swatch in sync
       no matter who wins the load race. Our own restoreSlide0() also mutates this <img>, but it
       pauses this observer around its write (disconnect + takeRecords + re-observe), so the
       restore can never be mistaken for a B4B swap and self-trigger. */
    var featuredSlide = slides[featuredIndex];
    if (featuredSlide && 'MutationObserver' in window) {
      var lastFeaturedColor = featuredColor;
      featObserver = new MutationObserver(function () {
        var c = resolvedColorOf(featuredIndex);
        if (!c || !ranges[c] || c === lastFeaturedColor) return;
        // On a processed B4B card, B4B moves the image AND the checked swatch together —
        // honour a swap only when the two agree. A resolved colour that CONTRADICTS the
        // checked swatch is a stale hint (e.g. the original alt text surviving a src
        // repaint) and must not steal the selection back to another swatch.
        if (card && card.classList && card.classList.contains('variable-products')) {
          var liveChecked = card.querySelector('.color-swatch__radio:checked');
          var liveColor = liveChecked ? (liveChecked.value || '').toLowerCase().trim() : null;
          if (liveColor && c !== liveColor) return;
        }
        lastFeaturedColor = c;
        openColor(c, featuredIndex);
      });
      featObserver.observe(featuredSlide, {
        attributes: true,
        attributeFilter: ['src', 'srcset'],
        subtree: true,
        childList: true
      });
    }

    /* Last: switch on parked-state isolation (only the .wd-cur slide paints at
       rest — see markMoving above). Added after the initial applyRange so a
       wd-cur slide is guaranteed to exist before neighbours get hidden. */
    el.classList.add('wd-ready');
  }

  /* Only initialise a slider (which groups images by colour and PRELOADS the active colour's
     neighbour images) once it is near the viewport. This is what keeps cards hidden behind
     "Load more" — and any card below the fold — from preloading images on page load; they init
     only when revealed / scrolled to. */
  var initIO = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            initSlider(entry.target);
            initIO.unobserve(entry.target);
          }
        });
      }, { rootMargin: '300px 0px' })
    : null;

  /* Cards inside the PDP product sliders must NOT go through the observer.
     Those sections render their cards into a wrapper that is display:none until
     roughly 4s after load (theme.js hides the wraps, then fades them in and
     slick-initialises them). An element that is display:none never intersects,
     so the observer would never fire and the card would stay a dead single
     slide — arrows inert, and the colour swatches falling through to the
     theme's own handler, which cannot drive slider markup and simply errors.
     These sections hold a handful of products, so initialising them eagerly is
     cheap and, unlike the observer, deterministic. */
  var CAROUSEL_SEL = '.slick-slider, .slick-track, .slick-list, ' +
    '.cross-sell-slider, .same-color-slider, .complete-look-slider, ' +
    '.recomend-slider, .recent-slider';

  function inCarouselSection(el) {
    return !!(el.closest && el.closest(CAROUSEL_SEL));
  }

  function isVisible(el) {
    return !!(el.offsetParent || el.getClientRects().length);
  }

  function initAll() {
    document.querySelectorAll('.product-image-slider').forEach(function (slider) {
      if (!inCarouselSection(slider)) {
        if (initIO) { initIO.observe(slider); } else { initSlider(slider); }
        return;
      }
      // Carousel card: init as soon as it is actually visible. While hidden we
      // skip it (leaving it unregistered) so a later pass picks it up — the
      // colour of the first image can only be read once it has rendered.
      if (isVisible(slider)) initSlider(slider);
    });
  }

  /* The PDP slider sections are revealed ~4s after load, so run initAll again at
     a few checkpoints. initSlider is idempotent (WeakSet-guarded) and initAll is
     a cheap querySelectorAll, so the repeats cost nothing on the collection page. */
  function initAllLater() {
    initAll();
    setTimeout(initAll, 4200);
    setTimeout(initAll, 6000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllLater);
  } else {
    initAllLater();
  }
  window.addEventListener('load', initAll);

  /* One observer for the grid area — initialise sliders as AJAX pagination / filters swap
     in new cards. Scoped to #facet-main (which persists across facet swaps; only its
     CHILDREN are replaced) instead of the whole body, so unrelated page churn (drawers,
     header, apps) no longer runs this addedNodes scan. Falls back to body if absent. */
  var pending = false;
  var mo = new MutationObserver(function (mutations) {
    var needInit = mutations.some(function (m) {
      return Array.prototype.slice.call(m.addedNodes).some(function (n) {
        return n.nodeType === 1 && (
          (n.classList && n.classList.contains('product-image-slider')) ||
          (n.querySelector && n.querySelector('.product-image-slider'))
        );
      });
    });
    if (!needInit || pending) return;
    pending = true;
    setTimeout(function () { pending = false; initAll(); }, 120);
  });

  mo.observe(document.getElementById('facet-main') || document.body, { childList: true, subtree: true });
}());

/* Lazy-hydrate the non-first slider images as each card scrolls near the viewport. */
(function () {
  function hydrateSlide(slide) {
    var img = slide.querySelector('img.lazy-image[data-src]');
    if (!img) return;

    var realSrc = img.getAttribute('data-src');
    var realSrcset = img.getAttribute('data-srcset');
    var realSizes = img.getAttribute('data-sizes');

    if (realSrcset) img.setAttribute('srcset', realSrcset);
    if (realSizes) img.setAttribute('sizes', realSizes);
    if (realSrc) img.setAttribute('src', realSrc);

    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-sizes');

    var finish = function () {
      img.classList.add('loaded');
      var placeholder = slide.querySelector('.slide-placeholder');
      if (placeholder) placeholder.classList.add('hidden');
    };

    if (img.complete && img.naturalWidth > 0) {
      finish();
    } else {
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
    }
  }

  // Load only the FIRST  images of each card up front. The rest hydrate on demand as the
  // user slides (the navigation script warms neighbours on every move), so the grid never
  // floods the network with every card's whole gallery at once — which was leaving the
  // visible images stuck in a long download queue.
  var HYDRATE_ON_LOAD = 1;
  function hydrateSlider(sliderEl) {
    if (sliderEl.dataset.wdHydrated === '1') return;
    sliderEl.dataset.wdHydrated = '1';
    var slides = sliderEl.querySelectorAll('.slider-slide:not(.slider-clone)');
    for (var i = 0; i < slides.length && i < HYDRATE_ON_LOAD; i++) {
      hydrateSlide(slides[i]);
    }
  }

  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            hydrateSlider(entry.target);
            io.unobserve(entry.target);
          }
        });
      }, { rootMargin: '600px 0px' })
    : null;

  /* Same display:none problem as the slider init above: cards in the PDP
     product sliders never intersect while their wrapper is hidden, so hydrate
     them directly instead of waiting on the observer. */
  var CAROUSEL_SEL = '.slick-slider, .slick-track, .slick-list, ' +
    '.cross-sell-slider, .same-color-slider, .complete-look-slider, ' +
    '.recomend-slider, .recent-slider';

  function observeAll(root) {
    var sliders = root.querySelectorAll('.product-image-slider');
    sliders.forEach(function (s) {
      if (io && !(s.closest && s.closest(CAROUSEL_SEL))) {
        io.observe(s);
      } else {
        hydrateSlider(s);
      }
    });
  }

  observeAll(document);

  /* Catch cards swapped in by AJAX pagination / filtering. Falls back to body so
     the PDP sliders (whose cards live in .cross-sell-slider etc., not a
     .product-list__inner) are covered too. */
  var observerTarget = document.querySelector('.product-list__inner') ||
                       document.querySelector('product-list') ||
                       document.body;
  if (observerTarget && 'MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) observeAll(node.parentElement ? node : document);
        });
      });
    }).observe(observerTarget, { childList: true, subtree: true });
  }
})();
